import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Card, Modal, formatPersianNumber } from '../../../app/components/foundation';
import type { Tenant } from '../types/tenant.types';
import { TenantStatusBadge } from './TenantStatusBadge';
import { useDeleteTenant } from '../hooks/useDeleteTenant';
import { useSuspendTenant } from '../hooks/useSuspendTenant';
import { useActivateTenant } from '../hooks/useActivateTenant';
import { useDeactivateTenant } from '../hooks/useDeactivateTenant';
import { TenantEditModal } from './TenantEditModal';


export interface TenantWorkspaceHeaderProps {
  tenant: Tenant;
  onUpdated: () => void;
}


export function TenantWorkspaceHeader({
  tenant,
  onUpdated,
}: TenantWorkspaceHeaderProps) {

  const navigate = useNavigate();
  const { submit, deleting, error } = useDeleteTenant();
const { submit: suspendSubmit, suspending, error: suspendError } = useSuspendTenant();
const { submit: activateSubmit, activating, error: activateError } = useActivateTenant();
const { submit: deactivateSubmit, deactivating, error: deactivateError } = useDeactivateTenant();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
const [isSuspendConfirmOpen, setIsSuspendConfirmOpen] = useState(false);
  const [isActivateConfirmOpen, setIsActivateConfirmOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

const isActive = tenant.status === 'active';


function handleSuspendClick() {
  setIsSuspendConfirmOpen(true);
}

function handleCancelSuspend() {
  if (suspending) return;
  setIsSuspendConfirmOpen(false);
}

async function handleConfirmSuspend() {
  try {
    await suspendSubmit(String(tenant.id));
    setIsSuspendConfirmOpen(false);
    window.location.reload();
  } catch {
    // hook owns error state
  }
}

function handleActivateClick() {
  setIsActivateConfirmOpen(true);
}

async function handleDeactivateClick() {
  try {
    await deactivateSubmit(String(tenant.id));
    window.location.reload();
  } catch {
    // hook owns error state
  }
}

function handleCancelActivate() {
  if (activating) return;
  setIsActivateConfirmOpen(false);
}

async function handleConfirmActivate() {
  try {
    await activateSubmit(String(tenant.id));
    setIsActivateConfirmOpen(false);
    window.location.reload();
  } catch {
    // hook owns error state
  }
}

function handleDeleteClick() {
    setIsConfirmOpen(true);
  }

  function handleCancelDelete() {
    if (deleting) return;
    setIsConfirmOpen(false);
  }

  async function handleConfirmDelete() {
    try {
      await submit(String(tenant.id));
      navigate('/tenants');
    } catch {
      // submit() already set the hook's error state; keep the
      // confirm dialog open so the message is visible.
    }
  }


  return (

    <Card className="vania-tenant-profile-header">

      <div className="vania-tenant-workspace-header">

        <div className="vania-tenant-workspace-header-main">


          <div className="vania-tenant-workspace-header-identity">

            <h1 className="vania-tenant-workspace-name">
              {tenant.name}
            </h1>

            <TenantStatusBadge status={tenant.status} />

          </div>



          <dl className="vania-tenant-workspace-meta">


            <div>
              <dt>
                شناسه تننت
              </dt>

              <dd dir="ltr">
                {formatPersianNumber(tenant.id)}
              </dd>

            </div>

            <div>
              <dt>موبایل نماینده</dt>
              <dd dir="ltr">{tenant.representative_mobile ?? '—'}</dd>
            </div>



            <div>

              <dt>
                اسلاگ
              </dt>

              <dd dir="ltr">
                {tenant.slug}
              </dd>

            </div>


          </dl>


        </div>



        <div className="vania-tenant-workspace-header-actions">
          <div className="vania-tenant-profile-action-group">


          {!isActive ? (
            <button
              className="vania-tenant-suspend-button vania-tenant-status-action vania-tenant-status-action--activate"
              type="button"
              onClick={handleActivateClick}
              disabled={activating}
            >
              {activating ? 'در حال فعال‌سازی...' : 'فعال کردن تننت'}
            </button>
          ) : (
            <button
              className="vania-tenant-suspend-button vania-tenant-status-action vania-tenant-status-action--suspend"
              type="button"
              onClick={handleSuspendClick}
              disabled={suspending}
            >
              {suspending ? 'در حال تعلیق...' : 'تعلیق تننت'}
            </button>
          )}

            {isActive && (
              <button
                className="vania-tenant-delete-button"
                type="button"
                onClick={handleDeactivateClick}
                disabled={deactivating}
              >
                {deactivating ? 'در حال غیرفعال‌سازی...' : 'غیرفعال کردن تننت'}
              </button>
            )}

            <button
              className="vania-tenant-create-submit"
              type="button"
              onClick={() => setIsEditOpen(true)}
            >
              ویرایش تننت
            </button>
          </div>

          <div className="vania-tenant-profile-action-group vania-tenant-profile-action-group--danger">
            <button
              className="vania-tenant-delete-button"
              type="button"
              onClick={handleDeleteClick}
              disabled={deleting}
            >
              {deleting ? 'در حال حذف...' : 'حذف تننت'}
            </button>
          </div>


        </div>


      </div>


      <Modal open={isSuspendConfirmOpen} onClose={handleCancelSuspend} title="تعلیق تننت">

        <div className="vania-tenant-delete-confirm">

          <p>
            آیا از تعلیق تننت «{tenant.name}» مطمئن هستید؟
          </p>

          {suspendError && (
            <p className="vania-tenant-delete-error">{suspendError}</p>
          )}

          <div className="vania-tenant-delete-confirm-actions">

            <button
              type="button"
              className="vania-tenant-create-cancel"
              onClick={handleCancelSuspend}
              disabled={suspending}
            >
              انصراف
            </button>


            <button
              type="button"
              className="vania-tenant-delete-button"
              onClick={handleConfirmSuspend}
              disabled={suspending}
            >
              {suspending ? 'در حال تعلیق...' : 'بله، تعلیق کن'}
            </button>

          </div>

        </div>

      </Modal>


      <Modal open={isActivateConfirmOpen} onClose={handleCancelActivate} title="فعال کردن تننت">

        <div className="vania-tenant-delete-confirm">

          <p>
            آیا از فعال کردن تننت «{tenant.name}» مطمئن هستید؟
          </p>

          {activateError && (
            <p className="vania-tenant-delete-error">{activateError}</p>
          )}

          <div className="vania-tenant-delete-confirm-actions">

            <button
              type="button"
              className="vania-tenant-create-cancel"
              onClick={handleCancelActivate}
              disabled={activating}
            >
              انصراف
            </button>


            <button
              type="button"
              className="vania-tenant-delete-button"
              onClick={handleConfirmActivate}
              disabled={activating}
            >
              {activating ? 'در حال فعال‌سازی...' : 'بله، فعال کن'}
            </button>

          </div>

        </div>

      </Modal>


      <Modal open={isConfirmOpen} onClose={handleCancelDelete} title="حذف تننت">

        <div className="vania-tenant-delete-confirm">

          <p>
            آیا از غیرفعال‌سازی نرم تننت «{tenant.name}» مطمئن هستید؟ رکورد حذف فیزیکی نمی‌شود.
          </p>

          {(error || deactivateError) && (
            <p className="vania-tenant-delete-error">{error || deactivateError}</p>
          )}

          <div className="vania-tenant-delete-confirm-actions">
            <button
              type="button"
              className="vania-tenant-create-cancel"
              onClick={handleCancelDelete}
              disabled={deleting}
            >
              انصراف
            </button>
            <button
              type="button"
              className="vania-tenant-delete-button"
              onClick={handleConfirmDelete}
              disabled={deleting}
            >
              {deleting ? 'در حال حذف...' : 'بله، حذف کن'}
            </button>
          </div>

        </div>

      </Modal>

      <TenantEditModal
        open={isEditOpen}
        tenant={tenant}
        onClose={() => setIsEditOpen(false)}
        onUpdated={onUpdated}
      />

    </Card>

  );
}
